/**
 * User-friendly error message mapping and formatting.
 * 
 * Maps technical API errors to user-friendly messages with actionable guidance.
 */

/**
 * Map API error responses to user-friendly messages.
 * 
 * @param error - Error object from API call
 * @param defaultMessage - Default message if error can't be mapped
 * @returns User-friendly error message
 */
export const getUserFriendlyError = (error: any, defaultMessage: string = 'An error occurred'): string => {
  // Extract error message from various error formats
  let errorMessage: string = '';
  let statusCode: number | null = null;
  
  if (error?.response?.data?.detail) {
    errorMessage = error.response.data.detail;
    statusCode = error.response.status;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
    statusCode = error.response.status;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }
  
  // Map common error patterns to user-friendly messages
  const errorMappings: Array<{ pattern: RegExp | string; message: string }> = [
    // Authentication errors
    { pattern: /invalid.*credential/i, message: 'The cloud credentials are incorrect. Please check your username, password, and project settings, then try again.' },
    { pattern: /authentication.*failed/i, message: 'Authentication failed. Please verify your cloud credentials are correct.' },
    { pattern: /unauthorized/i, message: 'You don\'t have permission to access this resource. Please check your credentials and permissions.' },
    { pattern: /forbidden|403/i, message: 'You don\'t have permission to perform this action. Please contact your administrator if you believe this is an error.' },
    
    // Network errors
    { pattern: /network.*error|failed.*fetch|timeout/i, message: 'Unable to connect to the server. Please check your internet connection and try again.' },
    { pattern: /connection.*refused/i, message: 'Unable to connect to the cloud service. Please check your network connection and the cloud service status.' },
    
    // Not found errors
    { pattern: /not.*found|404/i, message: 'The requested resource was not found. It may have been deleted or you may not have access to it.' },
    { pattern: /project.*not.*found/i, message: 'This project no longer exists or you don\'t have access to it. Please refresh your cloud list.' },
    { pattern: /cloud.*not.*found/i, message: 'This cloud configuration no longer exists. Please refresh the page.' },
    
    // Validation errors
    { pattern: /invalid.*format|malformed/i, message: 'The provided data format is invalid. Please check your input and try again.' },
    { pattern: /missing.*required/i, message: 'Required information is missing. Please fill in all required fields and try again.' },
    { pattern: /duplicate/i, message: 'This item already exists. Please check for duplicates or use a different name.' },
    
    // Server errors
    { pattern: /500|internal.*server.*error/i, message: 'A server error occurred. Please try again in a few moments. If the problem persists, contact support.' },
    { pattern: /503|service.*unavailable/i, message: 'The service is temporarily unavailable. Please try again in a few moments.' },
    { pattern: /502|bad.*gateway/i, message: 'The service is experiencing issues. Please try again in a few moments.' },
    
    // Rate limiting
    { pattern: /rate.*limit|429/i, message: 'Too many requests. Please wait a moment and try again.' },
    
    // OpenStack specific errors
    { pattern: /quota.*exceeded/i, message: 'Resource quota has been exceeded. Please free up some resources or contact your administrator.' },
    { pattern: /insufficient.*resources/i, message: 'Insufficient resources available. Please try again later or contact your administrator.' },
  ];
  
  // Check status code first for common HTTP errors
  if (statusCode) {
    switch (statusCode) {
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You don\'t have permission to perform this action. Please contact your administrator if you believe this is an error.';
      case 404:
        return 'The requested resource was not found. It may have been deleted or you may not have access to it.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'A server error occurred. Please try again in a few moments. If the problem persists, contact support.';
      case 502:
      case 503:
        return 'The service is temporarily unavailable. Please try again in a few moments.';
    }
  }
  
  // Try to match error message patterns
  const normalizedMessage = errorMessage.toLowerCase();
  for (const mapping of errorMappings) {
    if (typeof mapping.pattern === 'string') {
      if (normalizedMessage.includes(mapping.pattern.toLowerCase())) {
        return mapping.message;
      }
    } else {
      if (mapping.pattern.test(errorMessage) || mapping.pattern.test(normalizedMessage)) {
        return mapping.message;
      }
    }
  }
  
  // If no mapping found, return the original error message if it's user-friendly,
  // otherwise return default message
  if (errorMessage && errorMessage.length < 200 && !errorMessage.includes('stack trace')) {
    // Likely a user-friendly message already
    return errorMessage;
  }
  
  return defaultMessage;
};

/**
 * Extract error message from API error response.
 * 
 * @param error - Error object from API call
 * @param context - Context of the error (e.g., "loading projects", "deleting cloud")
 * @returns User-friendly error message with context
 */
export const getErrorWithContext = (error: any, context: string): string => {
  const friendlyMessage = getUserFriendlyError(error, `Failed to ${context}`);
  
  // If the friendly message already includes context, return as-is
  if (friendlyMessage.toLowerCase().includes(context.toLowerCase())) {
    return friendlyMessage;
  }
  
  // Otherwise, prepend context if it's a generic message
  if (friendlyMessage === 'An error occurred' || friendlyMessage.startsWith('Failed to')) {
    return `Failed to ${context}. ${friendlyMessage}`;
  }
  
  return friendlyMessage;
};
